<template>
  <div class="cropper-box" @mousedown.capture="beginLayoutInteraction" @touchstart.capture="beginLayoutInteraction">
    <vueCropper
      ref="cropper"
      :img="option.img"
      :output-size="option.size"
      :output-type="option.outputType"
      :info="true"
      :full="option.full"
      :can-move="option.canMove"
      :can-move-box="option.canMoveBox"
      :fixed-box="option.fixedBox"
      :fixed="option.fixed"
      :fixed-number="option.fixedNumber"
      :original="option.original"
      :auto-crop="option.autoCrop"
      :auto-crop-width="option.autoCropWidth"
      :auto-crop-height="option.autoCropHeight"
      :center-box="option.centerBox"
      :high="option.high"
      :info-true="option.infoTrue"
      :max-img-size="option.maxImgSize"
      :enlarge="option.enlarge"
      :mode="option.mode"
      @realTime="realTime"
      @imgLoad="imgLoad"
      @cropMoving="cropMoving"
    />
  </div>

</template>

<script>
import { markRaw } from 'vue'
import VueCropper from 'vue-cropper/src/vue-cropper.vue'
import {uploadImgBase64} from '@/api/attachment'

export default {
  name: 'CropperImg',
  components: {
    VueCropper
  },
  props: {
    fixed: {
      type: Boolean,
      default: true
    },
    fixedNumber: {
      type: Array,
      default: function() {
        return [1, 1]
      }
    },
    image: {
      type: String,
      default: ''
    },
    selectFileName: {
      type: String,
      default: ''
    },
    centerBox: {
      type: Boolean,
      default: false
    }

  },
  data() {
    return {
      layoutObserver: null,
      layoutFrame: 0,
      layoutActive: false,
      layoutImageReady: false,
      layoutInteracting: false,
      option: {
        img: 'http://cdn.xyxiao.cn/Landscape_2.jpg',
        size: 1,
        full: true,
        outputType: 'png',
        canMove: true,
        fixedBox: false,
        fixed: true,
        fixedNumber: [1, 1],
        original: false,
        canMoveBox: true,
        autoCrop: true,
        autoCropWidth: 0,
        autoCropHeight: 0,
        centerBox: false,
        high: false,
        cropData: {},
        enlarge: 1,
        mode: 'contain',
        maxImgSize: 2000,
        infoTrue: true
      }
    }
  },
  watch: {
    fixedNumber: {
      handler(newName, oldName) {
        this.option.fixedNumber = newName
      },
      immediate: true,
      deep: true
    },
    image: {
      handler(newName, oldName) {
        this.layoutImageReady = false
        this.option.img = newName
      },
      immediate: true,
      deep: true
    },
    fixed: {
      handler(newVal, oldVal) {
        this.option.fixed = newVal
      },
      immediate: true,
      deep: true
    },
    centerBox: {
      handler(newVal, oldVal) {
        this.option.centerBox = newVal
      },
      immediate: true,
      deep: true
    }
  },
  mounted() {
    this.layoutActive = true
    const container = this.getCropperContainer()
    if (typeof ResizeObserver !== 'undefined' && container) {
      this.layoutObserver = markRaw(new ResizeObserver(() => this.scheduleLayoutRefresh()))
      this.layoutObserver.observe(container)
    }
    window.addEventListener('resize', this.scheduleLayoutRefresh)
    window.addEventListener('orientationchange', this.scheduleLayoutRefresh)
    window.visualViewport?.addEventListener('resize', this.scheduleLayoutRefresh)
    this.scheduleLayoutRefresh()
  },
  beforeUnmount() {
    this.layoutActive = false
    this.layoutObserver?.disconnect()
    if (this.layoutFrame) window.cancelAnimationFrame(this.layoutFrame)
    window.removeEventListener('resize', this.scheduleLayoutRefresh)
    window.removeEventListener('orientationchange', this.scheduleLayoutRefresh)
    window.visualViewport?.removeEventListener('resize', this.scheduleLayoutRefresh)
    this.clearLayoutInteraction()
  },
  methods: {
    imgLoad(msg) {
      this.layoutImageReady = msg === 'success'
      if (this.layoutImageReady) this.scheduleLayoutRefresh()
    },
    realTime(data) {
      // console.log(data)
    },
    cropMoving(data) {
      this.option.cropData = data
    },
    getCropperContainer() {
      const cropper = this.$refs.cropper
      return cropper?.$refs?.cropper || cropper?.$el || null
    },
    beginLayoutInteraction() {
      if (this.layoutInteracting) return
      this.layoutInteracting = true
      window.addEventListener('mouseup', this.endLayoutInteraction)
      window.addEventListener('touchend', this.endLayoutInteraction)
      window.addEventListener('touchcancel', this.endLayoutInteraction)
      window.addEventListener('blur', this.endLayoutInteraction)
    },
    endLayoutInteraction(event) {
      if (event?.touches?.length) return
      this.clearLayoutInteraction()
      this.scheduleLayoutRefresh()
    },
    clearLayoutInteraction() {
      this.layoutInteracting = false
      window.removeEventListener('mouseup', this.endLayoutInteraction)
      window.removeEventListener('touchend', this.endLayoutInteraction)
      window.removeEventListener('touchcancel', this.endLayoutInteraction)
      window.removeEventListener('blur', this.endLayoutInteraction)
    },
    scheduleLayoutRefresh() {
      if (!this.layoutActive || this.layoutFrame) return
      this.layoutFrame = window.requestAnimationFrame(() => {
        this.layoutFrame = 0
        this.refreshLayout()
      })
    },
    // 保持选区对应的原图区域；只更新 vue-cropper 缓存的容器尺寸与仿射坐标。
    refreshLayout() {
      const cropper = this.$refs.cropper
      const container = this.getCropperContainer()
      if (!this.layoutActive || !container || !this.layoutImageReady || cropper.loading || this.layoutInteracting ||
        !cropper.trueWidth || !cropper.trueHeight) return false

      const style = window.getComputedStyle(container)
      const width = parseFloat(style.width)
      const height = parseFloat(style.height)
      if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return false

      const oldWidth = cropper.w
      const oldHeight = cropper.h
      if (Math.abs(width - oldWidth) < 0.1 && Math.abs(height - oldHeight) < 0.1) return false

      if (oldWidth <= 0 || oldHeight <= 0) {
        // 图片在隐藏容器内加载时尚无有效选区，此时才允许初始化自动选区。
        cropper.w = width
        cropper.h = height
        cropper.scale = cropper.original ? 1 : cropper.checkedMode()
        cropper.x = (width - cropper.trueWidth) / 2
        cropper.y = (height - cropper.trueHeight) / 2
        if (cropper.autoCrop) cropper.goAutoCrop()
        return true
      }

      const oldFit = cropper.original ? 1 : Math.min(1, oldWidth / cropper.trueWidth, oldHeight / cropper.trueHeight)
      const newFit = cropper.original ? 1 : Math.min(1, width / cropper.trueWidth, height / cropper.trueHeight)
      const hasCrop = cropper.cropping && cropper.cropW > 0 && cropper.cropH > 0
      const factor = Math.min(newFit / oldFit,
        hasCrop ? width / cropper.cropW : Infinity,
        hasCrop ? height / cropper.cropH : Infinity)
      if (!Number.isFinite(factor) || factor <= 0) return false

      let shiftX = (width - oldWidth * factor) / 2
      let shiftY = (height - oldHeight * factor) / 2
      if (hasCrop) {
        // 选区碰到容器边界时一起平移图片，选中的像素区域保持不变。
        const cropX = cropper.cropOffsertX * factor + shiftX
        const cropY = cropper.cropOffsertY * factor + shiftY
        shiftX += Math.max(0, Math.min(width - cropper.cropW * factor, cropX)) - cropX
        shiftY += Math.max(0, Math.min(height - cropper.cropH * factor, cropY)) - cropY
      }

      // vue-cropper 的 x/y 是缩放前图像坐标，需补偿中心缩放的偏移。
      cropper.x = cropper.x * factor + (factor - 1) * cropper.trueWidth / 2 + shiftX
      cropper.y = cropper.y * factor + (factor - 1) * cropper.trueHeight / 2 + shiftY
      cropper.scale *= factor
      cropper.cropW *= factor
      cropper.cropH *= factor
      cropper.cropOffsertX = cropper.cropOffsertX * factor + shiftX
      cropper.cropOffsertY = cropper.cropOffsertY * factor + shiftY
      cropper.w = width
      cropper.h = height
      return true
    },
        // 新增：设置裁剪框信息
    setAutoCropInfo(info) {
      if (!this.$refs.cropper) return
      
      const { width, height } = info
      
      // 如果 width 和 height 都为 0，表示使用原图尺寸
      if (width === 0 && height === 0) {
        // 获取图片容器信息
        this.$refs.cropper.getCropData((data) => {
          // 使用 goAutoCrop 方法重新初始化裁剪框为全图
          this.$refs.cropper.goAutoCrop()
        })
      } else {
        // 设置指定的宽高
        this.option.autoCropWidth = width
        this.option.autoCropHeight = height
        
        // 刷新裁剪框
        this.$nextTick(() => {
          if (this.$refs.cropper) {
            this.$refs.cropper.goAutoCrop()
          }
        })
      }
    },
    finish() {
      // 上传截图到服务器
      this.$refs.cropper.getCropData(data => {
        var param = {}
        param.file = data
        param.fileExt = this.option.outputType
        param.fileName = this.selectFileName
        this.$emit('uploadingBase64', true)
        uploadImgBase64(param).then(response => {
          this.$emit('uploadingBase64', false)
          if (response.data) {
            this.$emit('uploadSucceed', response.data)
          } else {
            this.$message({
              message: response,
              type: 'error'
            })
          }
        }).catch(e => {
          this.$emit('uploadingBase64', false)
        })
      })
    }
  }
}
</script>
<style rel="stylesheet/scss" lang="scss" scoped>
  .cropper-box {
    width: 100%;
    position: absolute;
    bottom: 0;
    top: 0;
  }
</style>
